import { JsonPathSelector } from "components/JsonPathSelector";
import {
  Box,
  Button,
  Field,
  Flex,
  HStack,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
} from "components/ui";
import { toaster } from "components/ui/toaster";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdAdd, MdContentCopy, MdDelete, MdRefresh } from "react-icons/md";

// Clickable placeholder tag that copies its value to clipboard on click
const PlaceholderTag = ({ value }: { value: string }) => (
  <Box
    as="span"
    display="inline"
    px="1"
    borderRadius="sm"
    fontFamily="mono"
    fontSize="xs"
    cursor="pointer"
    bg={{ base: "blue.50", _dark: "blue.900" }}
    color={{ base: "blue.600", _dark: "blue.300" }}
    borderWidth="1px"
    borderColor={{ base: "blue.200", _dark: "blue.700" }}
    title="Click to copy"
    onClick={() => {
      navigator.clipboard.writeText(value);
      toaster.create({
        title: `${value} copied`,
        type: "info",
        duration: 1500,
      });
    }}
  >
    {value}
  </Box>
);

// Splits a hint string on {placeholder} patterns and wraps each in a PlaceholderTag
const hintWithTags = (text: string) => {
  const parts = text.split(/(\{[^}]+\})/);
  return (
    <>
      {parts.map((part, i) =>
        /^\{[^}]+\}$/.test(part) ? (
          <PlaceholderTag key={i} value={part} />
        ) : (
          part
        ),
      )}
    </>
  );
};

// Generate a cryptographically strong random secret
export const generateSecret = (): string => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?";
  const array = new Uint8Array(64);
  window.crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join("");
};

interface TokenEndpointConfigProps {
  autoTokenEndpoint?: string;
  autoTokenMethod?: string;
  autoTokenRequestHeaders?: string;
  autoTokenRequestBody?: string;
  autoTokenRequestPlaceholders?: string;
  autoTokenResponseExample?: string;
  autoTokenResponsePath?: string;
  autoTokenHeaderName?: string;
  autoTokenHeaderFormat?: string;
  autoTokenSecret?: string;
  // Refresh token fields
  refreshTokenPath?: string;
  refreshTokenEndpoint?: string;
  refreshTokenMethod?: string;
  refreshTokenRequestHeaders?: string;
  refreshTokenRequestBody?: string;
  refreshTokenResponsePath?: string;
  onChange: (field: string, value: any) => void;
  disabled?: boolean;
  errors?: {
    autoTokenEndpoint?: string;
    autoTokenResponsePath?: string;
    autoTokenHeaderName?: string;
    autoTokenHeaderFormat?: string;
  };
}

export const TokenEndpointConfig = ({
  autoTokenEndpoint,
  autoTokenMethod = "POST",
  autoTokenRequestHeaders,
  autoTokenRequestBody,
  autoTokenRequestPlaceholders,
  autoTokenResponseExample,
  autoTokenResponsePath,
  autoTokenHeaderName,
  autoTokenHeaderFormat,
  autoTokenSecret,
  refreshTokenPath,
  refreshTokenEndpoint,
  refreshTokenMethod = "POST",
  refreshTokenRequestHeaders,
  refreshTokenRequestBody,
  refreshTokenResponsePath,
  onChange,
  disabled = false,
  errors,
}: TokenEndpointConfigProps) => {
  const { t } = useTranslation();

  // Parse headers
  const parseHeaders = (
    json?: string,
  ): Array<{ name: string; value: string }> => {
    if (!json) return [];
    try {
      const parsed = JSON.parse(json);
      return Object.entries(parsed).map(([name, value]) => ({
        name,
        value: String(value),
      }));
    } catch {
      return [];
    }
  };

  const [headers, setHeaders] = useState<
    Array<{ name: string; value: string }>
  >(parseHeaders(autoTokenRequestHeaders));

  useEffect(() => {
    setHeaders(parseHeaders(autoTokenRequestHeaders));
  }, [autoTokenRequestHeaders]);

  const handleHeadersChange = (
    newHeaders: Array<{ name: string; value: string }>,
  ) => {
    setHeaders(newHeaders);
    if (newHeaders.length === 0) {
      onChange("autoTokenRequestHeaders", "");
    } else {
      const obj = newHeaders.reduce(
        (acc, { name, value }) => {
          if (name.trim()) acc[name] = value;
          return acc;
        },
        {} as Record<string, string>,
      );
      onChange("autoTokenRequestHeaders", JSON.stringify(obj));
    }
  };

  // Parse placeholders
  const parsePlaceholders = (
    json?: string,
  ): Array<{ key: string; label: string }> => {
    if (!json) return [];
    try {
      const parsed = JSON.parse(json);
      return Object.entries(parsed).map(([key, label]) => ({
        key,
        label: String(label),
      }));
    } catch {
      return [];
    }
  };

  const [placeholders, setPlaceholders] = useState<
    Array<{ key: string; label: string }>
  >(parsePlaceholders(autoTokenRequestPlaceholders));

  useEffect(() => {
    setPlaceholders(parsePlaceholders(autoTokenRequestPlaceholders));
  }, [autoTokenRequestPlaceholders]);

  const handlePlaceholdersChange = (
    newPlaceholders: Array<{ key: string; label: string }>,
  ) => {
    setPlaceholders(newPlaceholders);
    if (newPlaceholders.length === 0) {
      onChange("autoTokenRequestPlaceholders", "");
    } else {
      const obj = newPlaceholders.reduce(
        (acc, { key, label }) => {
          if (key.trim()) acc[key] = label;
          return acc;
        },
        {} as Record<string, string>,
      );
      onChange("autoTokenRequestPlaceholders", JSON.stringify(obj));
    }
  };

  // Refresh token headers
  const [refreshHeaders, setRefreshHeaders] = useState<
    Array<{ name: string; value: string }>
  >(parseHeaders(refreshTokenRequestHeaders));

  useEffect(() => {
    setRefreshHeaders(parseHeaders(refreshTokenRequestHeaders));
  }, [refreshTokenRequestHeaders]);

  const handleRefreshHeadersChange = (
    newHeaders: Array<{ name: string; value: string }>,
  ) => {
    setRefreshHeaders(newHeaders);
    if (newHeaders.length === 0) {
      onChange("refreshTokenRequestHeaders", "");
    } else {
      const obj = newHeaders.reduce(
        (acc, { name, value }) => {
          if (name.trim()) acc[name] = value;
          return acc;
        },
        {} as Record<string, string>,
      );
      onChange("refreshTokenRequestHeaders", JSON.stringify(obj));
    }
  };

  const [refreshEnabled, setRefreshEnabled] = useState(!!refreshTokenEndpoint);

  useEffect(() => {
    if (refreshTokenEndpoint) setRefreshEnabled(true);
  }, [refreshTokenEndpoint]);

  return (
    <VStack
      align="stretch"
      gap={4}
      p={4}
      borderWidth="1px"
      borderRadius="md"
      themeAware
    >
      <HStack themeAware>
        <Text fontSize="md" fontWeight="bold">
          {t("app.servers.authorization.tokenEndpoint.title")}
        </Text>
      </HStack>

      {/* HTTP Method and Endpoint */}
      <HStack gap={3} themeAware>
        <Field
          label={t("app.servers.authorization.tokenEndpoint.httpMethod")}
          flex="0 0 120px"
        >
          <Select
            items={[
              { label: "GET", value: "GET" },
              { label: "POST", value: "POST" },
              { label: "PUT", value: "PUT" },
            ]}
            value={[autoTokenMethod || "POST"]}
            onValueChange={(details) =>
              onChange("autoTokenMethod", details.value[0])
            }
            disabled={disabled}
            size="sm"
          />
        </Field>
        <Field
          label={t("app.servers.authorization.tokenEndpoint.tokenEndpointUrl")}
          required
          flex={1}
          invalid={!!errors?.autoTokenEndpoint}
          errorText={errors?.autoTokenEndpoint}
        >
          <Input
            value={autoTokenEndpoint || ""}
            onChange={(e) => onChange("autoTokenEndpoint", e.target.value)}
            placeholder={t(
              "app.servers.authorization.tokenEndpoint.tokenEndpointUrlPlaceholder",
            )}
            disabled={disabled}
            size="sm"
          />
        </Field>
      </HStack>

      {/* Request Headers */}
      <Box themeAware>
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontSize="sm" fontWeight="medium">
            {t("app.servers.authorization.tokenEndpoint.requestHeaders")}
          </Text>
          <Button
            size="xs"
            onClick={() =>
              handleHeadersChange([...headers, { name: "", value: "" }])
            }
            colorPalette="blue"
            variant="outline"
            disabled={disabled}
          >
            <MdAdd />
            {t("app.servers.authorization.tokenEndpoint.addHeader")}
          </Button>
        </Flex>
        <Text fontSize="xs" mb={2}>
          {hintWithTags(
            t("app.servers.authorization.tokenEndpoint.requestHeadersHint"),
          )}
        </Text>
        <VStack gap={2} themeAware>
          {headers.map((header, index) => (
            <HStack key={index} gap={2} w="full" themeAware>
              <Input
                placeholder={t(
                  "app.servers.authorization.tokenEndpoint.headerNamePlaceholder",
                )}
                value={header.name}
                onChange={(e) => {
                  const updated = [...headers];
                  updated[index].name = e.target.value;
                  handleHeadersChange(updated);
                }}
                flex={1}
                size="sm"
                disabled={disabled}
              />
              <Input
                placeholder={t(
                  "app.servers.authorization.tokenEndpoint.headerValuePlaceholder",
                )}
                value={header.value}
                onChange={(e) => {
                  const updated = [...headers];
                  updated[index].value = e.target.value;
                  handleHeadersChange(updated);
                }}
                flex={1}
                size="sm"
                disabled={disabled}
              />
              <Button
                size="sm"
                colorPalette="red"
                variant="ghost"
                onClick={() =>
                  handleHeadersChange(headers.filter((_, i) => i !== index))
                }
                disabled={disabled}
              >
                <MdDelete />
              </Button>
            </HStack>
          ))}
          {headers.length === 0 && (
            <Text fontSize="xs">
              {t("app.servers.authorization.tokenEndpoint.noHeadersConfigured")}
            </Text>
          )}
        </VStack>
      </Box>

      {/* Request Body */}
      <Field
        label={t("app.servers.authorization.tokenEndpoint.requestBody")}
        helperText={hintWithTags(
          t("app.servers.authorization.tokenEndpoint.requestBodyHint"),
        )}
      >
        <Textarea
          value={autoTokenRequestBody || ""}
          onChange={(e) => onChange("autoTokenRequestBody", e.target.value)}
          placeholder={t(
            "app.servers.authorization.tokenEndpoint.requestBodyPlaceholder",
          )}
          rows={4}
          disabled={disabled}
          fontFamily="mono"
          fontSize="sm"
        />
      </Field>

      {/* Custom Placeholders */}
      <Box themeAware>
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontSize="sm" fontWeight="medium">
            {t("app.servers.authorization.tokenEndpoint.customPlaceholders")}
          </Text>
          <Button
            size="xs"
            onClick={() =>
              handlePlaceholdersChange([
                ...placeholders,
                { key: "", label: "" },
              ])
            }
            colorPalette="blue"
            variant="outline"
            disabled={disabled}
          >
            <MdAdd />
            {t("app.servers.authorization.tokenEndpoint.addPlaceholder")}
          </Button>
        </Flex>
        <Text fontSize="xs" mb={2}>
          {t("app.servers.authorization.tokenEndpoint.customPlaceholdersHint")}
        </Text>
        <VStack gap={2} themeAware>
          {placeholders.map((placeholder, index) => (
            <HStack key={index} gap={2} w="full" themeAware>
              <Input
                placeholder={t(
                  "app.servers.authorization.tokenEndpoint.placeholderKeyPlaceholder",
                )}
                value={placeholder.key}
                onChange={(e) => {
                  const updated = [...placeholders];
                  updated[index].key = e.target.value;
                  handlePlaceholdersChange(updated);
                }}
                flex={1}
                size="sm"
                disabled={disabled}
              />
              <Input
                placeholder={t(
                  "app.servers.authorization.tokenEndpoint.placeholderLabelPlaceholder",
                )}
                value={placeholder.label}
                onChange={(e) => {
                  const updated = [...placeholders];
                  updated[index].label = e.target.value;
                  handlePlaceholdersChange(updated);
                }}
                flex={1}
                size="sm"
                disabled={disabled}
              />
              <Button
                size="sm"
                colorPalette="red"
                variant="ghost"
                onClick={() =>
                  handlePlaceholdersChange(
                    placeholders.filter((_, i) => i !== index),
                  )
                }
                disabled={disabled}
              >
                <MdDelete />
              </Button>
            </HStack>
          ))}
          {placeholders.length === 0 && (
            <Text fontSize="xs">
              {t(
                "app.servers.authorization.tokenEndpoint.noPlaceholdersDefined",
              )}
            </Text>
          )}
        </VStack>
      </Box>

      {/* Response Configuration */}
      <Field
        label={t("app.servers.authorization.tokenEndpoint.sampleResponseJson")}
        helperText={t(
          "app.servers.authorization.tokenEndpoint.sampleResponseHint",
        )}
      >
        <Textarea
          value={autoTokenResponseExample || ""}
          onChange={(e) => onChange("autoTokenResponseExample", e.target.value)}
          placeholder={t(
            "app.servers.authorization.tokenEndpoint.sampleResponsePlaceholder",
          )}
          rows={6}
          disabled={disabled}
          fontFamily="mono"
          fontSize="sm"
        />
      </Field>

      {/* Token Response Path - Always visible */}
      <Field
        label={t("app.servers.authorization.tokenEndpoint.tokenResponsePath")}
        required
        helperText={
          !errors?.autoTokenResponsePath
            ? t("app.servers.authorization.tokenEndpoint.tokenResponsePathHint")
            : undefined
        }
        invalid={!!errors?.autoTokenResponsePath}
        errorText={errors?.autoTokenResponsePath}
      >
        <Input
          value={autoTokenResponsePath || ""}
          onChange={(e) => onChange("autoTokenResponsePath", e.target.value)}
          placeholder={t(
            "app.servers.authorization.tokenEndpoint.tokenResponsePathPlaceholder",
          )}
          disabled={disabled}
          size="sm"
          fontFamily="mono"
        />
      </Field>

      {autoTokenResponseExample && (
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            {t("app.servers.authorization.tokenEndpoint.selectTokenField")}
          </Text>
          <JsonPathSelector
            jsonExample={autoTokenResponseExample}
            selectedPath={autoTokenResponsePath || null}
            onPathSelect={(path) => onChange("autoTokenResponsePath", path)}
          />
        </Box>
      )}

      {/* Authorization Header Configuration */}
      <HStack gap={3} themeAware>
        <Field
          label={t("app.servers.authorization.tokenEndpoint.authHeaderName")}
          required
          flex={1}
          helperText={
            !errors?.autoTokenHeaderName
              ? hintWithTags(
                  t(
                    "app.servers.authorization.tokenEndpoint.authHeaderNameHint",
                  ),
                )
              : undefined
          }
          invalid={!!errors?.autoTokenHeaderName}
          errorText={errors?.autoTokenHeaderName}
        >
          <Input
            value={autoTokenHeaderName || ""}
            onChange={(e) => onChange("autoTokenHeaderName", e.target.value)}
            placeholder={t(
              "app.servers.authorization.tokenEndpoint.authHeaderNamePlaceholderSimple",
            )}
            disabled={disabled}
            size="sm"
          />
        </Field>
        <Field
          label={t("app.servers.authorization.tokenEndpoint.authHeaderFormat")}
          required
          flex={1}
          helperText={
            !errors?.autoTokenHeaderFormat
              ? hintWithTags(
                  t(
                    "app.servers.authorization.tokenEndpoint.authHeaderFormatHint",
                  ),
                )
              : undefined
          }
          invalid={!!errors?.autoTokenHeaderFormat}
          errorText={errors?.autoTokenHeaderFormat}
        >
          <Input
            value={autoTokenHeaderFormat || ""}
            onChange={(e) => onChange("autoTokenHeaderFormat", e.target.value)}
            placeholder={t(
              "app.servers.authorization.tokenEndpoint.authHeaderFormatPlaceholderSimple",
            )}
            disabled={disabled}
            size="sm"
          />
        </Field>
      </HStack>

      {/* Secret Configuration */}
      <Field
        label={t("app.servers.authorization.tokenEndpoint.secretValue")}
        required
      >
        <HStack gap={2} w="full" themeAware>
          <Input
            type="text"
            value={autoTokenSecret || ""}
            readOnly
            placeholder={t(
              "app.servers.authorization.tokenEndpoint.secretPlaceholder",
            )}
            disabled={disabled}
            size="sm"
            flex={1}
            fontFamily="mono"
            fontSize="xs"
          />
          <Button
            size="sm"
            colorPalette="blue"
            variant="outline"
            onClick={() => {
              if (autoTokenSecret) {
                navigator.clipboard.writeText(autoTokenSecret);
                toaster.create({
                  title: t("app.common.copied"),
                  description: t(
                    "app.servers.authorization.tokenEndpoint.secretCopied",
                  ),
                  type: "success",
                  duration: 2000,
                });
              }
            }}
            disabled={disabled || !autoTokenSecret}
          >
            <MdContentCopy />
          </Button>
          <Button
            size="sm"
            colorPalette="green"
            variant="outline"
            onClick={() => {
              const newSecret = generateSecret();
              onChange("autoTokenSecret", newSecret);
              toaster.create({
                title: t("app.common.success"),
                description: t(
                  "app.servers.authorization.tokenEndpoint.secretRegenerated",
                ),
                type: "success",
                duration: 2000,
              });
            }}
            disabled={disabled}
          >
            <MdRefresh />
          </Button>
        </HStack>
      </Field>

      {/* Refresh Token Configuration */}
      <Box themeAware borderWidth="1px" borderRadius="md" p={3}>
        <Flex
          justify="space-between"
          align="center"
          mb={refreshEnabled ? 3 : 0}
        >
          <Text fontSize="sm" fontWeight="bold">
            {t("app.servers.authorization.tokenEndpoint.refreshToken.title")}
          </Text>
          <Button
            size="xs"
            colorPalette={refreshEnabled ? "red" : "blue"}
            variant="outline"
            onClick={() => {
              if (refreshEnabled) {
                setRefreshEnabled(false);
                onChange("refreshTokenEndpoint", "");
                onChange("refreshTokenMethod", "");
                onChange("refreshTokenPath", "");
                onChange("refreshTokenRequestHeaders", "");
                onChange("refreshTokenRequestBody", "");
                onChange("refreshTokenResponsePath", "");
              } else {
                setRefreshEnabled(true);
                onChange("refreshTokenMethod", "POST");
                if (!refreshTokenResponsePath && autoTokenResponsePath) {
                  onChange("refreshTokenResponsePath", autoTokenResponsePath);
                }
              }
            }}
            disabled={disabled}
          >
            {refreshEnabled
              ? t(
                  "app.servers.authorization.tokenEndpoint.refreshToken.disable",
                )
              : t(
                  "app.servers.authorization.tokenEndpoint.refreshToken.enable",
                )}
          </Button>
        </Flex>

        {!refreshEnabled && (
          <Text fontSize="xs">
            {t(
              "app.servers.authorization.tokenEndpoint.refreshToken.disabledHint",
            )}
          </Text>
        )}

        {refreshEnabled && (
          <VStack align="stretch" gap={3} themeAware>
            {/* Refresh token path in initial response */}
            <Field
              label={t(
                "app.servers.authorization.tokenEndpoint.refreshToken.tokenPath",
              )}
              helperText={hintWithTags(
                t(
                  "app.servers.authorization.tokenEndpoint.refreshToken.tokenPathHint",
                ),
              )}
            >
              <Input
                value={refreshTokenPath || ""}
                onChange={(e) => onChange("refreshTokenPath", e.target.value)}
                placeholder="refresh_token"
                disabled={disabled}
                size="sm"
                fontFamily="mono"
              />
            </Field>

            {autoTokenResponseExample && (
              <Box>
                <JsonPathSelector
                  jsonExample={autoTokenResponseExample}
                  selectedPath={refreshTokenPath || null}
                  onPathSelect={(path) => onChange("refreshTokenPath", path)}
                />
              </Box>
            )}

            {/* Refresh endpoint: method + URL */}
            <HStack gap={3} themeAware>
              <Field
                label={t("app.servers.authorization.tokenEndpoint.httpMethod")}
                flex="0 0 120px"
              >
                <Select
                  items={[
                    { label: "GET", value: "GET" },
                    { label: "POST", value: "POST" },
                    { label: "PUT", value: "PUT" },
                  ]}
                  value={[refreshTokenMethod || "POST"]}
                  onValueChange={(details) =>
                    onChange("refreshTokenMethod", details.value[0])
                  }
                  disabled={disabled}
                  size="sm"
                />
              </Field>
              <Field
                label={t(
                  "app.servers.authorization.tokenEndpoint.refreshToken.endpoint",
                )}
                flex={1}
              >
                <Input
                  value={refreshTokenEndpoint || ""}
                  onChange={(e) =>
                    onChange("refreshTokenEndpoint", e.target.value)
                  }
                  placeholder="https://api.example.com/auth/refresh"
                  disabled={disabled}
                  size="sm"
                />
              </Field>
            </HStack>

            {/* Refresh request headers */}
            <Box themeAware>
              <Flex justify="space-between" align="center" mb={2}>
                <Text fontSize="sm" fontWeight="medium">
                  {t("app.servers.authorization.tokenEndpoint.requestHeaders")}
                </Text>
                <Button
                  size="xs"
                  onClick={() =>
                    handleRefreshHeadersChange([
                      ...refreshHeaders,
                      { name: "", value: "" },
                    ])
                  }
                  colorPalette="blue"
                  variant="outline"
                  disabled={disabled}
                >
                  <MdAdd />
                  {t("app.servers.authorization.tokenEndpoint.addHeader")}
                </Button>
              </Flex>
              <Text fontSize="xs" mb={2}>
                {hintWithTags(
                  t(
                    "app.servers.authorization.tokenEndpoint.refreshToken.requestHeadersHint",
                  ),
                )}
              </Text>
              <VStack gap={2} themeAware>
                {refreshHeaders.map((header, index) => (
                  <HStack key={index} gap={2} w="full" themeAware>
                    <Input
                      placeholder={t(
                        "app.servers.authorization.tokenEndpoint.headerNamePlaceholder",
                      )}
                      value={header.name}
                      onChange={(e) => {
                        const updated = [...refreshHeaders];
                        updated[index].name = e.target.value;
                        handleRefreshHeadersChange(updated);
                      }}
                      flex={1}
                      size="sm"
                      disabled={disabled}
                    />
                    <Input
                      placeholder={t(
                        "app.servers.authorization.tokenEndpoint.headerValuePlaceholder",
                      )}
                      value={header.value}
                      onChange={(e) => {
                        const updated = [...refreshHeaders];
                        updated[index].value = e.target.value;
                        handleRefreshHeadersChange(updated);
                      }}
                      flex={1}
                      size="sm"
                      disabled={disabled}
                    />
                    <Button
                      size="sm"
                      colorPalette="red"
                      variant="ghost"
                      onClick={() =>
                        handleRefreshHeadersChange(
                          refreshHeaders.filter((_, i) => i !== index),
                        )
                      }
                      disabled={disabled}
                    >
                      <MdDelete />
                    </Button>
                  </HStack>
                ))}
                {refreshHeaders.length === 0 && (
                  <Text fontSize="xs">
                    {t(
                      "app.servers.authorization.tokenEndpoint.noHeadersConfigured",
                    )}
                  </Text>
                )}
              </VStack>
            </Box>

            {/* Refresh request body */}
            <Field
              label={t("app.servers.authorization.tokenEndpoint.requestBody")}
              helperText={hintWithTags(
                t(
                  "app.servers.authorization.tokenEndpoint.refreshToken.requestBodyHint",
                ),
              )}
            >
              <Textarea
                value={refreshTokenRequestBody || ""}
                onChange={(e) =>
                  onChange("refreshTokenRequestBody", e.target.value)
                }
                placeholder={'{"refresh_token": "{refresh_token}"}'}
                rows={3}
                disabled={disabled}
                fontFamily="mono"
                fontSize="sm"
              />
            </Field>

            {/* Refresh response path */}
            <Field
              label={t(
                "app.servers.authorization.tokenEndpoint.refreshToken.responsePath",
              )}
              helperText={t(
                "app.servers.authorization.tokenEndpoint.refreshToken.responsePathHint",
              )}
            >
              <Input
                value={refreshTokenResponsePath || ""}
                onChange={(e) =>
                  onChange("refreshTokenResponsePath", e.target.value)
                }
                placeholder="access_token"
                disabled={disabled}
                size="sm"
                fontFamily="mono"
              />
            </Field>

            {autoTokenResponseExample && (
              <Box>
                <JsonPathSelector
                  jsonExample={autoTokenResponseExample}
                  selectedPath={refreshTokenResponsePath || null}
                  onPathSelect={(path) =>
                    onChange("refreshTokenResponsePath", path)
                  }
                />
              </Box>
            )}
          </VStack>
        )}
      </Box>
    </VStack>
  );
};
